import React, { useState } from "react";
import { Link } from "react-router-dom";
import { recordsApps, receiptsApps } from "../components/Constants";
import { useUser } from "../contexts/UserContext";

const NavigationPage = () => {
  const [activeTab, setActiveTab] = useState("receipts");
  const { userData } = useUser();

  // Check specific permissions for different app types
  const hasReceiptsPermission = () => {
    if (!userData?.permissions?.invoiceSystem?.receipts) return false;

    const { receipts } = userData.permissions.invoiceSystem;
    return (
      receipts.invoicesSubAccess ||
      receipts.noticeChargesAccess ||
      receipts.ackReceiptsAccess
    );
  };

  const hasRecordsPermission = () => {
    if (!userData?.permissions?.invoiceSystem?.records) return false;

    const { records } = userData.permissions.invoiceSystem;

    // Check internal permissions
    const hasInternal =
      records.internal?.caseBriefAccess ||
      records.internal?.executiveAdminAccess ||
      records.internal?.secretarialLogsAccess ||
      records.internal?.processServerLogsAccess;

    // Check external permissions
    const hasExternal =
      records.external?.transmittalAccess ||
      records.external?.legalAdviceAccess ||
      records.external?.secretariatResolutionsAccess;

    return hasInternal || hasExternal;
  };

  // Check permissions for specific app based on its requirements
  const isAppDisabled = (app) => {
    // If app is already disabled, keep it disabled
    if (app.disabled) return true;

    // If no user data or permissions, disable everything
    if (!userData?.permissions?.invoiceSystem) return true;

    const { invoiceSystem } = userData.permissions;

    // Check specific app permissions based on app name
    switch (app.name) {
      // Receipts Apps
      case "Invoices":
        return !invoiceSystem.receipts?.invoicesSubAccess;

      case "Notice of Charges":
        return !invoiceSystem.receipts?.noticeChargesAccess;

      case "Acknowledgement Receipts":
        return !invoiceSystem.receipts?.ackReceiptsAccess;

      // External Records Apps
      case "Transmittal":
        return !invoiceSystem.records?.external?.transmittalAccess;

      case "Legal Advice":
        return !invoiceSystem.records?.external?.legalAdviceAccess;

      case "Secretariat Resolutions":
        return !invoiceSystem.records?.external?.secretariatResolutionsAccess;

      // Internal Records Apps
      case "Case Brief":
        return !invoiceSystem.records?.internal?.caseBriefAccess;

      case "Executive & Administrative":
        return !invoiceSystem.records?.internal?.executiveAdminAccess;

      case "Secretarial Records":
        return !invoiceSystem.records?.internal?.secretarialLogsAccess;

      case "Process Server & Messenger Logs":
        return !invoiceSystem.records?.internal?.processServerLogsAccess;

      default:
        // If app name doesn't match, keep original disabled state
        return false;
    }
  };

  const AppCard = ({ app }) => {
    const isDisabled = isAppDisabled(app);

    return (
      <div className="relative group overflow-hidden shadow-lg transition-all duration-300 ease-out bg-white/10 border border-white/20 rounded-2xl hover:shadow-2xl hover:-translate-y-1 hover:bg-white/15 hover:border-tier-secondary">
        <Link
          to={isDisabled ? "#" : app.path}
          className={`block h-full p-6 text-center transition-colors duration-300 ${
            isDisabled
              ? "cursor-default pointer-events-none"
              : "hover:bg-white/10"
          }`}
        >
          {app.category && (
            <div className="absolute top-2 right-2 bg-white/10 text-xs px-2 py-1 rounded-full text-alt-color z-10">
              {app.category}
            </div>
          )}

          <div className="flex flex-col items-center justify-center min-h-[200px]">
            <span className="material-symbols-outlined text-5xl text-tier-secondary mb-4">
              {app.icon}
            </span>
            <h3 className="text-lg font-semibold mb-1">{app.name}</h3>
            <div className="w-1/2 h-px bg-white/20 my-2" />
            <p className="text-sm text-alt-color">{app.description}</p>
          </div>
        </Link>

        {isDisabled && (
          <div className="absolute inset-0 bg-black/70 text-white flex flex-col items-center justify-center text-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <span className="material-symbols-outlined text-4xl mb-1">
              {app.disabled ? "construction" : "no_accounts"}
            </span>
            <p className="text-sm font-semibold">
              {app.disabled ? "Coming Soon" : "Access Denied"}
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col">
      {/* Tab Navigation */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center mb-8">
          <div className="custom-card p-1 flex rounded-lg">
            <button
              onClick={() => setActiveTab("receipts")}
              disabled={!hasReceiptsPermission()}
              className={`px-8 py-4 rounded-lg font-semibold transition-all duration-300 ${
                activeTab === "receipts"
                  ? "bg-white/20 text-color shadow-lg"
                  : "text-alt-color hover:text-color hover:bg-white/10"
              } ${
                !hasReceiptsPermission() ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              Receipts
            </button>
            <button
              onClick={() => setActiveTab("records")}
              disabled={!hasRecordsPermission()}
              className={`px-8 py-4 rounded-lg font-semibold transition-all duration-300 ${
                activeTab === "records"
                  ? "bg-white/20 text-color shadow-lg"
                  : "text-alt-color hover:text-color hover:bg-white/10"
              } ${
                !hasRecordsPermission() ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              Records
            </button>
          </div>
        </div>

        {/* App Grid */}
        <main className="flex-1">
          {activeTab === "receipts" && hasReceiptsPermission() && (
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {receiptsApps.map((app, index) => (
                  <AppCard key={index} app={app} />
                ))}
              </div>
            </div>
          )}

          {activeTab === "records" && hasRecordsPermission() && (
            <div className="max-w-6xl mx-auto">
              {/* External Records Section */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold mb-6 text-center flex items-center justify-center">
                  <span className="material-symbols-outlined mr-3 text-2xl">
                    public
                  </span>
                  External Records
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {recordsApps
                    .filter((app) => app.category === "External")
                    .map((app, index) => (
                      <AppCard key={index} app={app} />
                    ))}
                </div>
              </div>

              {/* Internal Records Section */}
              <div>
                <h2 className="text-2xl font-bold mb-6 text-center flex items-center justify-center">
                  <span className="material-symbols-outlined mr-3 text-2xl">
                    lock
                  </span>
                  Internal Records
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                  {recordsApps
                    .filter((app) => app.category === "Internal")
                    .map((app, index) => (
                      <AppCard key={index} app={app} />
                    ))}
                </div>
              </div>
            </div>
          )}

          {/* No Access Message */}
          {activeTab === "receipts" && !hasReceiptsPermission() && (
            <div className="flex flex-col items-center justify-center py-16">
              <span className="material-symbols-outlined text-6xl text-gray-400 mb-4">
                no_accounts
              </span>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                Access Denied
              </h3>
              <p className="text-gray-500">
                You don't have permission to access Receipts.
              </p>
            </div>
          )}

          {activeTab === "records" && !hasRecordsPermission() && (
            <div className="flex flex-col items-center justify-center py-16">
              <span className="material-symbols-outlined text-6xl text-gray-400 mb-4">
                no_accounts
              </span>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                Access Denied
              </h3>
              <p className="text-gray-500">
                You don't have permission to access Records.
              </p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default NavigationPage;
